// src/Sidebar.jsx
import React, { useState, useContext } from "react";
import { AuthContext } from "./contexts/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FiHome,
  FiBell,
  FiMessageSquare,
  FiEdit,
  FiBookOpen,
  FiBookmark,
  FiUser,
  FiSettings,
  FiInfo,
  FiTrendingUp,
  FiHelpCircle,
  FiBriefcase,
  FiShield,
  FiFileText,
  FiCheckSquare
} from "react-icons/fi";
import PostWarningModal from "./PostWarningModal";
import SearchField from "./components/SearchField";
import logoImage from "./assets/logo.png";
import { NotificationContext } from "./contexts/NotificationContext";
import { hasSeenPostWarning, markPostWarningSeen } from "./utils/postWarning";

// Added onItemClick prop to gracefully close mobile panels on selection
export default function Sidebar({ onItemClick, searchQuery, setSearchQuery }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPostWarning, setShowPostWarning] = useState(false);

  const CREATE_ROUTE = "/create";
  const { accessToken, user } = useContext(AuthContext);
  const isLoggedIn = !!accessToken || !!user;

  const { hasUnread } = useContext(NotificationContext) || { hasUnread: false };

  const handleCreateClick = (e) => {
    if (onItemClick) onItemClick(); // Close mobile drawer first

    if (!isLoggedIn) {
      e.preventDefault();
      navigate("/login", {
        state: { from: location.pathname },
      });
      return;
    }

    if (hasSeenPostWarning(user)) return;

    e.preventDefault();
    setShowPostWarning(true);
  };

  const handleContinue = () => {
    markPostWarningSeen(user);
    setShowPostWarning(false);
    navigate(CREATE_ROUTE);
  };

  const handleCancel = () => {
    setShowPostWarning(false);
  };

  const handleLinkClick = () => {
    if (onItemClick) onItemClick();
  };

  const navLinkClass = "flex items-center gap-4 text-gray-900 hover:text-orange-600 transition text-[17px] font-medium py-1";
  const footerLinkClass = "flex items-center gap-3 hover:text-orange-500 transition text-sm md:text-xs text-gray-500 md:text-gray-400";
  const authButtonClass = "w-full rounded-full px-4 py-2.5 transition text-sm";

  return (
    <>
      {isLoggedIn && (
        <PostWarningModal
          open={showPostWarning}
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      )}

      <div className="flex h-full flex-col bg-white px-6 py-4 overflow-y-auto no-scrollbar justify-between">
        {!onItemClick && <div className="hidden md:block shrink-0 mb-5">
          <Link to="/" className="flex items-center gap-2 mb-5 text-lg font-semibold text-orange-600 tracking-wide">
            <img src={logoImage} alt="Recettes Logo" className="h-7 w-7 object-contain" />
            <span>Recettes</span>
          </Link>
          <SearchField searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>}
        {isLoggedIn ? (
          <>
            <nav className="flex flex-col space-y-5 pt-2">
              <Link to="/" className={navLinkClass} onClick={handleLinkClick}>
                <FiHome size={24} className="shrink-0 text-gray-800" />
                <span>Home</span>
              </Link>

              <Link to="/notifications" className={`${navLinkClass} relative`} onClick={handleLinkClick}>
                <div className="relative shrink-0">
                  <FiBell size={24} className="text-gray-800" />
                  {hasUnread && (
                    <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-orange-600" />
                  )}
                </div>
                <span>Notifications</span>
              </Link>

              <Link to="/chat" className={navLinkClass} onClick={handleLinkClick}>
                <FiMessageSquare size={24} className="shrink-0 text-gray-800" />
                <span>Messages</span>
              </Link>

              <Link to="/my-recipes" className={navLinkClass} onClick={handleLinkClick}>
                <FiBookOpen size={24} className="shrink-0 text-gray-800" />
                <span>My Recipes</span>
              </Link>

              <Link to="/saved-posts" className={navLinkClass} onClick={handleLinkClick}>
                <FiBookmark size={24} className="shrink-0 text-gray-800" />
                <span>Saved</span>
              </Link>

              <Link to="/profile" className={navLinkClass} onClick={handleLinkClick}>
                <FiUser size={24} className="shrink-0 text-gray-800" />
                <span>Profile</span>
              </Link>

              <Link to="/settings" className={navLinkClass} onClick={handleLinkClick}>
                <FiSettings size={24} className="shrink-0 text-gray-800" />
                <span>Settings</span>
              </Link>

              <div className="pt-4">
                <Link
                  to={CREATE_ROUTE}
                  onClick={handleCreateClick}
                  className="inline-flex w-full md:w-[170px] items-center justify-center gap-2 py-3 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold rounded-2xl shadow-lg shadow-orange-500/10 transition duration-300 transform hover:scale-[1.02]"
                >
                  <FiEdit size={18} />
                  <span className="text-[17px]">Create</span>
                </Link>
              </div>
            </nav>

            <div className="flex-1 min-h-[40px]" />

            <div className="space-y-3.5 border-t border-gray-100 pt-5 px-1 pb-2">
              <Link to="/about" className={footerLinkClass} onClick={handleLinkClick}>
                <FiInfo size={16} className="shrink-0" /> <span>About Recettes</span>
              </Link>
              <Link to="/advertise" className={footerLinkClass} onClick={handleLinkClick}>
                <FiTrendingUp size={16} className="shrink-0" /> <span>Advertise</span>
              </Link>
              <Link to="/help" className={footerLinkClass} onClick={handleLinkClick}>
                <FiHelpCircle size={16} className="shrink-0" /> <span>Help</span>
              </Link>
              <Link to="/careers" className={footerLinkClass} onClick={handleLinkClick}>
                <FiBriefcase size={16} className="shrink-0" /> <span>Careers</span>
              </Link>
              <Link to="/privacy" className={footerLinkClass} onClick={handleLinkClick}>
                <FiShield size={16} className="shrink-0" /> <span>Privacy Policy</span>
              </Link>
              <Link to="/terms" className={footerLinkClass} onClick={handleLinkClick}>
                <FiFileText size={16} className="shrink-0" /> <span>Terms of Service</span>
              </Link>
              <Link to="/community-guidelines" className={footerLinkClass} onClick={handleLinkClick}>
                <FiCheckSquare size={16} className="shrink-0" /> <span>Community Guidelines</span>
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6 space-y-3 pt-2">
              <button
                onClick={() => { navigate("/signup"); handleLinkClick(); }}
                className={`${authButtonClass} bg-orange-500 font-semibold text-white hover:bg-orange-600`}
              >
                Create account
              </button>

              <button
                onClick={() => { navigate("/login", { state: { from: location.pathname } }); handleLinkClick(); }}
                className={`${authButtonClass} bg-gray-100 text-gray-700 font-medium hover:bg-gray-200`}
              >
                Sign in
              </button>
            </div>

            <div className="flex-1 min-h-[40px]" />

            <div className="space-y-3.5 border-t border-gray-100 pt-5 px-1 pb-2">
              <Link to="/about" className={footerLinkClass} onClick={handleLinkClick}>
                <FiInfo size={16} className="shrink-0" /> <span>About Recettes</span>
              </Link>
              <Link to="/advertise" className={footerLinkClass} onClick={handleLinkClick}>
                <FiTrendingUp size={16} className="shrink-0" /> <span>Advertise</span>
              </Link>
              <Link to="/help" className={footerLinkClass} onClick={handleLinkClick}>
                <FiHelpCircle size={16} className="shrink-0" /> <span>Help</span>
              </Link>
              <Link to="/careers" className={footerLinkClass} onClick={handleLinkClick}>
                <FiBriefcase size={16} className="shrink-0" /> <span>Careers</span>
              </Link>
              <Link to="/privacy" className={footerLinkClass} onClick={handleLinkClick}>
                <FiShield size={16} className="shrink-0" /> <span>Privacy Policy</span>
              </Link>
              <Link to="/terms" className={footerLinkClass} onClick={handleLinkClick}>
                <FiFileText size={16} className="shrink-0" /> <span>Terms of Service</span>
              </Link>
              <Link to="/community-guidelines" className={footerLinkClass} onClick={handleLinkClick}>
                <FiCheckSquare size={16} className="shrink-0" /> <span>Community Guidelines</span>
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
}
